import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Copy,
  ExternalLink,
  Loader2,
  Mail,
  Monitor,
  Smartphone,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatItalianDateTime } from "@/lib/format";

type Props = {
  open: boolean;
  slug: string;
  onClose: () => void;
};

type Viewport = "desktop" | "mobile";

export function EmailPreviewDialog({ open, slug, onClose }: Props) {
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [tab, setTab] = useState<"html" | "plain">("html");
  const [copied, setCopied] = useState(false);

  const q = useQuery({
    queryKey: ["email-preview", slug],
    queryFn: () => api.getEmailPreview(slug),
    enabled: open && Boolean(slug),
    staleTime: 10_000,
  });

  if (!open) return null;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative flex h-[92vh] w-full max-w-5xl flex-col rounded-lg bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-moving-500" />
            <h2 className="text-lg font-semibold text-slate-900">
              Anteprima email
            </h2>
            {q.data?.size_bytes ? (
              <span className="text-xs text-slate-500">
                · {(q.data.size_bytes / 1024).toFixed(1)} KB
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            {/* Switch viewport */}
            <div className="mr-2 flex overflow-hidden rounded-md border border-slate-200">
              <button
                onClick={() => setViewport("desktop")}
                className={cn(
                  "flex items-center gap-1 px-2 py-1 text-xs",
                  viewport === "desktop"
                    ? "bg-moving-500 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50",
                )}
                title="Vista desktop (600px)"
              >
                <Monitor className="h-3.5 w-3.5" />
                Desktop
              </button>
              <button
                onClick={() => setViewport("mobile")}
                className={cn(
                  "flex items-center gap-1 px-2 py-1 text-xs",
                  viewport === "mobile"
                    ? "bg-moving-500 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50",
                )}
                title="Vista mobile (375px)"
              >
                <Smartphone className="h-3.5 w-3.5" />
                Mobile
              </button>
            </div>
            {/* Switch HTML / Plain */}
            <div className="mr-2 flex overflow-hidden rounded-md border border-slate-200">
              <button
                onClick={() => setTab("html")}
                className={cn(
                  "px-2 py-1 text-xs",
                  tab === "html"
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50",
                )}
              >
                HTML
              </button>
              <button
                onClick={() => setTab("plain")}
                className={cn(
                  "px-2 py-1 text-xs",
                  tab === "plain"
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50",
                )}
              >
                Plain text
              </button>
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Chiudi"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Subject + Preheader */}
        {q.data ? (
          <div className="border-b border-slate-100 bg-slate-50 px-5 py-3 text-sm">
            <div className="flex items-start gap-2">
              <span className="shrink-0 text-xs font-semibold uppercase text-slate-500">
                Oggetto:
              </span>
              <span className="font-medium text-slate-900">
                {q.data.subject || "(vuoto)"}
              </span>
              <button
                onClick={() => copyToClipboard(q.data!.subject)}
                className="ml-auto text-slate-400 hover:text-slate-700"
                title="Copia oggetto"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            {q.data.preheader ? (
              <div className="mt-1 flex items-start gap-2 text-xs">
                <span className="shrink-0 font-semibold uppercase text-slate-500">
                  Preheader:
                </span>
                <span className="text-slate-600">{q.data.preheader}</span>
              </div>
            ) : null}
            {copied ? (
              <div className="mt-1 text-[10px] text-moving-700">
                ✓ copiato
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Body */}
        <div className="flex-1 overflow-auto bg-slate-200 p-4">
          {q.isLoading ? (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carico email…
            </div>
          ) : q.isError ? (
            <div className="mx-auto flex max-w-lg flex-col items-center gap-3 rounded-md border border-red-200 bg-red-50 p-6 text-center">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <div>
                <div className="font-medium text-red-900">
                  Email non disponibile
                </div>
                <div className="mt-1 text-sm text-red-700">
                  {String(
                    (q.error as { body?: { detail?: string } })?.body?.detail ??
                      q.error,
                  )}
                </div>
              </div>
            </div>
          ) : q.data ? (
            tab === "html" ? (
              <div
                className={cn(
                  "mx-auto h-full overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm",
                  viewport === "desktop" ? "max-w-[640px]" : "max-w-[400px]",
                )}
              >
                <iframe
                  title="Email preview"
                  srcDoc={q.data.html}
                  className="h-full w-full"
                  sandbox="allow-same-origin"
                />
              </div>
            ) : (
              <pre className="mx-auto max-w-3xl whitespace-pre-wrap rounded-md border border-slate-300 bg-white p-4 font-mono text-xs leading-relaxed text-slate-800">
                {q.data.plain_text || "(nessun plain text)"}
              </pre>
            )
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <div className="text-xs text-slate-500">
            {q.data?.generated_at
              ? `Generata: ${formatItalianDateTime(q.data.generated_at)}`
              : ""}
          </div>
          <div className="flex gap-2">
            {q.data?.html ? (
              <a
                href={`data:text/html;charset=utf-8,${encodeURIComponent(q.data.html)}`}
                download={`email_${slug}.html`}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Scarica HTML
              </a>
            ) : null}
            <Button variant="outline" size="sm" onClick={onClose}>
              Chiudi
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
